import { ApexOptions } from "apexcharts";
import React from "react";
import ReactApexChart from "react-apexcharts";
import { Button, CheckPicker, CheckTreePicker, FormGroup, Notification, Radio, RadioGroup } from "rsuite";
import axios from "../../../../utilities/axios";
import { YearNode } from "../../Types/YearNodeType";
import styles from "./styles/lineChartPerMonth.module.scss";
import { generateYearsTreePicker } from "./utils/generateYearsTreePicker";
import { CheckPickerSelectFilter } from "../../../../components/formfields/CheckPickerSelectFilter";
import { MethodsUtils } from "../../../../core/utils/MethodsUtils";
import handleGenerateHarmoniousColors from "../../utils/handleGenerateRandomColor";

interface LineChartPerMonthProps {
    stores: { label: string; value: number }[];
    platforms: { label: string; value: number }[];
    showFilters: boolean;
    errors: { label: string; value: number; hasValue: boolean; }[];
}

interface LineChartPerMonthState {
    errors: { label: string; value: number, count: number }[];
    data: { count: number, value: number }[][];
    series: {
        name: string;
        error: string;
        color: string;
        data: number[];
    }[];
    options: ApexOptions;
    filters: {
        fullPeriod: string[],
        employee: number[],
        store: number[],
        platform: number[],
        lineChartFilterType: string,
        errors: number[],
    },
    loadingButton: boolean;
    yearsSelectPicker: YearNode[];
}

class LineChartPerMonth extends React.Component<LineChartPerMonthProps, LineChartPerMonthState> {
    public state: LineChartPerMonthState = {
        errors: [],
        data: [],
        series: [],
        options: {
            chart: {
                height: 350,
                type: 'line',
                zoom: {
                    enabled: false
                }
            },
            colors: [],
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'straight'
            },
            grid: {
                row: {
                    colors: ['#f3f3f3', 'transparent'],
                    opacity: 0.5
                },
            },
            xaxis: {
                categories: ['Semana 1', 'Semana 2', 'Semana 3', 'Semana 4'],
            },
            tooltip: {
                y: {
                    formatter: (val, opts) => {
                        const data = this.state.data?.[opts.seriesIndex]?.[opts.dataPointIndex]
                        if (this.state.errors.length > 0) return `${MethodsUtils.formatCurrency(data?.value || 0)} (${val})`;
                        return `${val}`;
                    },
                }
            }
        },
        filters: {
            fullPeriod: ['2024'],
            employee: [],
            store: [],
            platform: [],
            lineChartFilterType: "",
            errors: [],
        },
        loadingButton: false,
        yearsSelectPicker: []
    }

    async componentDidMount() {
        const treePickerData = generateYearsTreePicker()

        this.setState({
            yearsSelectPicker: treePickerData
        })
    }

    handleGetChartData = async () => {
        this.setState({
            loadingButton: true
        })

        try {
            const { filters } = this.state;
            if (!filters?.lineChartFilterType) {
                Notification.warning({
                    title: "Aviso!",
                    description: "Selecione Loja ou Plataforma para buscar as informações",
                    duration: 3500
                })

                this.setState({
                    loadingButton: false
                })

                return;
            }

            if (filters.fullPeriod.length === 0) {
                Notification.warning({
                    title: "Aviso!",
                    description: "Selecione um período para buscar as informações",
                    duration: 3500
                })

                this.setState({
                    loadingButton: false
                })

                return;
            }

            const { fullPeriod, employee, lineChartFilterType, platform, store, errors } = filters;

            const lineChartPerMonthResponse = await axios.get("/api/v1/dashboard/get_linechartpermonth_information", {
                params: {
                    fullPeriod: fullPeriod ? fullPeriod : [],
                    lineChartFilterType: lineChartFilterType ? lineChartFilterType : "",
                    employee: employee ? employee : [],
                    store: store ? store : [],
                    platform: platform ? platform : [],
                    errors: errors ? errors : [],
                }
            })

            const { errors: errorData, data, series, categories, colors } = lineChartPerMonthResponse.data;

            const formattedCategories = categories.map((date: any) => {
                const [year, month] = date.split('-');
                return `${month}/${year}`;
            });

            this.setState({
                errors: errorData,
                data: data,
                series: series,
                options: {
                    ...this.state.options,
                    colors: handleGenerateHarmoniousColors(series.length),
                    xaxis: {
                        categories: formattedCategories,
                    },
                }
            })

        } catch (error) {
            Notification.error({
                title: "Erro!",
                description: "Erro ao buscar informação para o gráfico de linha",
                duration: 3500
            })
            console.log("Erro ao pegar dados para o gráfico de linha", error)
        }

        this.setState({
            loadingButton: false
        })
    }

    render() {
        return (
            <>
                <div className={styles.lineChart}>
                    {!this.props.showFilters && (
                        <>
                            <div className={styles.lineChartMonthFilters}>
                                <div className={styles.fieldsFiltersDivisor}>
                                    <FormGroup controlId="radioList">
                                        <span>‎ </span>
                                        <RadioGroup
                                            name="radioList"
                                            inline
                                            style={{ display: 'flex' }}
                                            value={this.state.filters.lineChartFilterType}
                                            onChange={(value) =>
                                                this.setState((prevState) => ({
                                                    filters: {
                                                        ...prevState.filters,
                                                        lineChartFilterType: value,
                                                    },
                                                }))
                                            }>
                                            <Radio value="store">Loja</Radio>
                                            <Radio value="platform">Plataforma</Radio>
                                        </RadioGroup>
                                    </FormGroup>

                                    {this.state.filters.lineChartFilterType === 'store' && (
                                        <div className={styles.filterDiv}>
                                            <span>Lojas</span>
                                            <CheckPicker
                                                data={this.props.stores}
                                                placeholder="Lojas para filtro..."
                                                style={{ width: '100%' }}
                                                value={this.state.filters.store}
                                                onChange={(value) =>
                                                    this.setState((prevState) => ({
                                                        filters: {
                                                            ...prevState.filters,
                                                            store: value,
                                                        },
                                                    }))
                                                }
                                            />
                                            <span className={styles.informationText}>
                                                *Se nenhum filtro for aplicado, todos os registros serão incluídos
                                            </span>
                                        </div>
                                    )}

                                    {this.state.filters.lineChartFilterType === 'platform' && (
                                        <div className={styles.filterDiv}>
                                            <span>Plataformas</span>
                                            <CheckPicker
                                                data={this.props.platforms}
                                                placeholder="Plataformas para filtro..."
                                                style={{ width: '100%' }}
                                                value={this.state.filters.platform}
                                                onChange={(value) =>
                                                    this.setState((prevState) => ({
                                                        filters: {
                                                            ...prevState.filters,
                                                            platform: value,
                                                        },
                                                    }))
                                                }
                                            />
                                            <span className={styles.informationText}>
                                                *Se nenhum filtro for aplicado, todos os registros serão incluídos
                                            </span>
                                        </div>
                                    )}

                                    <div className={styles.filterDiv}>
                                        <span>Lançamentos</span>
                                        <CheckPickerSelectFilter
                                            onSelectFilter={(values) => {
                                                this.setState((prevState) => ({
                                                    filters: {
                                                        ...prevState.filters,
                                                        errors: values,
                                                    },
                                                }))
                                            }}
                                            data={this.props.errors}
                                            placeholder="Lançamentos para filtro..."
                                            style={{ width: '100%' }}
                                            value={this.state.filters.errors}
                                            onChange={(value) =>
                                                this.setState((prevState) => ({
                                                    filters: {
                                                        ...prevState.filters,
                                                        errors: value,
                                                    },
                                                }))
                                            }
                                        />
                                        <span className={styles.informationText}>
                                            *Se nenhum filtro for aplicado, todos os registros serão incluídos
                                        </span>
                                    </div>
                                </div>

                                <div className={styles.filterButtons}>
                                    <Button
                                        appearance="primary"
                                        color="blue"
                                        loading={this.state.loadingButton}
                                        onClick={this.handleGetChartData}
                                        style={{ width: '200px' }}
                                    >
                                        Filtrar
                                    </Button>
                                </div>
                            </div>
                            <div>
                                <CheckTreePicker
                                    data={this.state.yearsSelectPicker}
                                    style={{ width: '100%' }}
                                    placeholder="Período para consulta..."
                                    value={this.state.filters.fullPeriod}
                                    onChange={(value) => {
                                        this.setState({
                                            filters: {
                                                ...this.state.filters,
                                                fullPeriod: value
                                            }
                                        })
                                    }}
                                    locale={{
                                        ...CheckTreePicker.defaultProps?.locale,
                                        checkAll: 'Ano Todo',
                                        selectedValues: 'Tudo',
                                    }}
                                />
                            </div>
                        </>
                    )}
                    <div className={styles.lineChartContainer}>
                        <div style={{ width: '100%' }}>
                            <div id="chart">
                                <ReactApexChart
                                    options={this.state.options}
                                    series={this.state.series}
                                    type="line"
                                    height={350}
                                />
                            </div>
                            <div id="html-dist"></div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {this.state.errors.map((e: any) => {
                            const color = this.state.series.find(item => item.name === e.label)?.color
                            return (
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }} key={e.label}>
                                    <div style={{ width: 10, height: 10, borderRadius: 5, background: color || 'white' }}></div>
                                    <span><strong>{e.label}: </strong>{MethodsUtils.formatCurrency(e.value)} ({e.count})</span>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </>
        )
    }
}

export default LineChartPerMonth;
